import React from 'react'

interface OptionCardProps {
  selected: boolean
  onClick: () => void
  title: string
  description?: string
  icon?: React.ReactNode
  /** Tailwind classes for the icon tile (bg + text). */
  tile?: string
  /**
   * Compact horizontal layout: icon + title on one row, description beneath —
   * so more cards fit on screen without scrolling. Defaults to false (icon tile
   * stacked above the title).
   */
  horizontal?: boolean
}

/**
 * #693: shared selectable card used across the onboarding wizard steps, matching
 * the payhelm.com/onboard mockup (pastel icon tile, indigo selected ring).
 */
const OptionCard: React.FC<OptionCardProps> = ({ selected, onClick, title, description, icon, tile, horizontal }) => {
  const ring = selected ? 'tw-border-indigo-500 tw-ring-2 tw-ring-indigo-200' : 'tw-border-slate-200'

  if (horizontal) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-pressed={selected}
        className={`tw-text-left tw-rounded-xl tw-border tw-bg-white tw-p-4 tw-transition tw-shadow-sm hover:tw-shadow-md hover:tw-border-slate-300 ${ring}`}
      >
        <div className="tw-flex tw-items-start tw-gap-3">
          {icon && (
            <div className={`tw-flex tw-h-9 tw-w-9 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-lg ${tile || 'tw-bg-indigo-50 tw-text-indigo-600'}`}>
              {icon}
            </div>
          )}
          <div className="tw-min-w-0">
            <div className="tw-text-sm tw-font-semibold tw-text-slate-900">{title}</div>
            {description && <div className="tw-mt-0.5 tw-text-xs tw-leading-relaxed tw-text-slate-500">{description}</div>}
          </div>
        </div>
      </button>
    )
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`tw-text-left tw-rounded-xl tw-border tw-bg-white tw-p-5 tw-transition tw-shadow-sm hover:tw-shadow-md hover:tw-border-slate-300 ${ring}`}
    >
      {icon && (
        <div className={`tw-mb-3 tw-flex tw-h-11 tw-w-11 tw-items-center tw-justify-center tw-rounded-xl ${tile || 'tw-bg-indigo-50 tw-text-indigo-600'}`}>
          {icon}
        </div>
      )}
      <div className="tw-text-base tw-font-semibold tw-text-slate-900 tw-mb-1">{title}</div>
      {description && <div className="tw-text-sm tw-leading-relaxed tw-text-slate-500">{description}</div>}
    </button>
  )
}

export default OptionCard
